// Cliente de /api/oficina/pelotas (backend/src/api/pelotas.routes.ts; contrato en docs/pelotas/ux-2026-11-03/
// construccion.md): los contratos con Santi, cada conversación, el chat con Santi, los borradores del agente (entrega 2)
// y los actos de Pau (escribir, pedir algo, Hecho/Cancelar, marcar visto). Lo que sale hacia Santi lleva
// `Idempotency-Key`: un reintento con la misma clave no lo repite.
import { api } from '../../../lib/api';
import type { AgenteSantiDTO, HiloDTO, HiloGeneralDTO, ListaContratosDTO, MotivoCierre } from './tipos';

const BASE = '/api/oficina/pelotas';

/**
 * Lo que acepta el servidor de la oficina (backend/src/api/pelotas.routes.ts: `Pedir`, `Mensaje`, `Cerrar`; el centro
 * admite lo mismo o más: título 3-120, detalle 20 000). Las cajas no dejan escribir más (maxLength) y avisan cerca del
 * límite; si aun así el servidor dice que no, el aviso dice qué sobra. Si cambian allí, cambian aquí.
 */
export const LIMITES = { titulo: 120, texto: 4000, nota: 2000 } as const;

export const pelotasApi = {
  lista: () => api<ListaContratosDTO>(BASE),
  hilo: (temaId: number) => api<HiloDTO>(`${BASE}/temas/${temaId}`),
  general: () => api<HiloGeneralDTO>(`${BASE}/temas/general`),
  /**
   * Un mensaje de Pau a Santi. En un contrato que le toca a Pau, enviar le pasa el turno a Santi (la oficina deriva lo
   * que le toca); `seguirYo` («No, sigo yo») manda solo el mensaje.
   */
  mensaje: (hilo: number | 'general', cuerpo: { texto: string; seguirYo?: boolean }, clave: string) =>
    api<{ seq: number; en: string; lado: string | null }>(`${BASE}/temas/${hilo}/mensajes`, {
      method: 'POST',
      body: cuerpo.seguirYo ? { texto: cuerpo.texto, seguirYo: true } : { texto: cuerpo.texto },
      headers: { 'Idempotency-Key': clave },
    }),
  /** «Hecho» o «Cancelar» (cualquier contrato abierto; nota vacía → «Hecho.» / «Cancelado.»). Sale al irse la línea de 8 s. */
  cerrar: (temaId: number, cuerpo: { nota?: string; motivo: MotivoCierre }, clave: string) =>
    api<{ seq: number; en: string }>(`${BASE}/temas/${temaId}/cerrar`, {
      method: 'POST',
      body: cuerpo.nota ? { nota: cuerpo.nota, motivo: cuerpo.motivo } : { motivo: cuerpo.motivo },
      headers: { 'Idempotency-Key': clave },
    }),
  /**
   * «Pedir algo a Santi»: abre un contrato pedido por Pau con el turno en Santi (título y «qué falta» los deriva la oficina
   * si faltan). `borrador`: el agente cuyo pedido preparado era lo que sale (el servidor lo usa y lo borra).
   */
  pedir: (cuerpo: { titulo?: string; texto: string; fechaLimite?: string; borrador?: string }, clave: string) =>
    api<{ temaId: number }>(`${BASE}/temas`, { method: 'POST', body: cuerpo, headers: { 'Idempotency-Key': clave } }),
  /** Pau ha visto hasta `hasta` (el último seq) en ese contrato o en el chat: se apaga el punto blanco. Solo su sesión. */
  visto: (hilo: number | 'general', hasta: number) => api<unknown>(`${BASE}/temas/${hilo}/visto`, { method: 'POST', body: { hasta } }),
  /** El agente de un contrato («Contratar un agente para esto»; si ya tiene, el suyo) o el de todo lo de Santi (`general`). */
  agente: (hilo: number | 'general') => api<{ agentId: string }>(`${BASE}/temas/${hilo}/agente`, { method: 'POST', body: {} }),
  /** «Que lo prepare tu agente» / «Otra versión»: el agente de Santi redacta para ese hilo; el borrador llega por el socket (202). */
  preparar: (hilo: number | 'general') => api<unknown>(`${BASE}/temas/${hilo}/preparar`, { method: 'POST', body: {} }),
  /** «Descartar» el borrador del agente de la caja de ese hilo. */
  descartarBorrador: (hilo: number | 'general') => api<unknown>(`${BASE}/temas/${hilo}/borrador/descartar`, { method: 'POST', body: {} }),
  /** Lo que la web pinta en el chat de un agente: su «Pedir algo» preparado («… · Revisar»), o null. */
  agenteSanti: (agentId: string) => api<AgenteSantiDTO>(`${BASE}/agentes/${encodeURIComponent(agentId)}`),
  /** «Descartar» el «Pedir algo» que preparó ese agente. */
  descartarPedido: (agentId: string) => api<unknown>(`${BASE}/agentes/${encodeURIComponent(agentId)}/pedido/descartar`, { method: 'POST', body: {} }),
};
